/**
 * @file order-status.ts
 *
 * Order-status vocabulary for the Orders tab: the ordered status list (for the
 * filter and the drawer's status select) and the accessors that resolve a status
 * to its Gravity indicator colour (consumed by the shared {@link StatusBadge})
 * and its localized label. The colour/label maps are module-private; callers use
 * {@link statusIndicator} / {@link statusLabel} so the fallback lives in one
 * place. The indicator colours mirror the Website Builder v2 OrdersTab.
 */

import type { MessageDescriptor } from 'react-intl';
import { adminMessages } from '../i18n/messages';

/** The order statuses, in display order (used by the filter and status select). */
export const ORDER_STATUSES = [
  'pending',
  'processing',
  'on-hold',
  'completed',
  'cancelled',
  'refunded',
  'failed',
] as const;

/** Indicator class used for an unrecognised status. */
export const DEFAULT_STATUS_INDICATOR = 'gv-state-busy';

/** Gravity indicator class per status; falls back to {@link DEFAULT_STATUS_INDICATOR}. */
const STATUS_INDICATOR: Record<string, string> = {
  pending: 'gv-state-busy',
  processing: 'gv-state-busy',
  'on-hold': 'gv-state-attention',
  completed: 'gv-state-positive',
  cancelled: 'gv-state-critical',
  refunded: 'gv-state-informative',
  failed: 'gv-state-critical',
};

/** Localized label descriptor per status. */
const STATUS_LABELS: Record<string, MessageDescriptor> = {
  pending: adminMessages.ordersStatusPending,
  processing: adminMessages.ordersStatusProcessing,
  'on-hold': adminMessages.ordersStatusOnHold,
  completed: adminMessages.ordersStatusCompleted,
  cancelled: adminMessages.ordersStatusCancelled,
  refunded: adminMessages.ordersStatusRefunded,
  failed: adminMessages.ordersStatusFailed,
};

/**
 * Resolves the Gravity indicator class for a status.
 *
 * @param status - The order status.
 *
 * @returns The `gv-state-*` class, or {@link DEFAULT_STATUS_INDICATOR}.
 */
export function statusIndicator(status: string): string {
  return STATUS_INDICATOR[status] ?? DEFAULT_STATUS_INDICATOR;
}

/**
 * Resolves the localized label descriptor for a status, or `undefined` when the
 * status is unrecognised (callers fall back to the raw status string).
 *
 * @param status - The order status.
 *
 * @returns The label {@link MessageDescriptor}, or `undefined`.
 */
export function statusLabel(status: string): MessageDescriptor | undefined {
  return STATUS_LABELS[status];
}
